import { Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent, RaIslandComponent } from 'ra-ui';
import { AiEditStore } from '../../../services/ai-edit.store';

/**
 * Ilha sobre a prévia no modo "Editar com IA":
 * - estado 3: escura, "IA editando · N de M alterações";
 * - estado 4: verde, "N alterações aplicadas · Desfazer", por 8 s (pausa com o mouse);
 * - estados 5 e 6: escura, "N de M aplicadas · 1 conflito aguardando você · 1 falhou",
 *   enquanto houver pendência (a decisão fica nos cartões do chat).
 *
 * Projetada no palco da prévia (`previewOverlay`), centrada no rodapé dele.
 */
@Component({
    selector: 'ai-edit-preview-island',
    imports: [RaIslandComponent, RaIconComponent, TranslatePipe],
    template: `
        @if (applying()) {
            <ra-island class="ai-island" role="status" animate.enter="island-in" animate.leave="island-out">
                <span class="island-spinner" aria-hidden="true"></span>
                <span class="ra-island-text">
                    {{ 'document.edit.aiEdit.islandApplying' | translate: { done: store.appliedCount(), total: total() } }}
                </span>
            </ra-island>
        } @else if (store.undoOffer(); as offer) {
            <ra-island
                class="ai-island ai-island--success"
                role="status"
                animate.enter="island-in"
                animate.leave="island-out"
                (mouseenter)="store.pauseUndo()"
                (mouseleave)="store.resumeUndo()">
                <ra-icon icon="tick" varColor="contentInverse" [size]="16" />
                <span class="ra-island-text">
                    {{ (offer.count === 1 ? 'document.edit.aiEdit.changesAppliedOne' : 'document.edit.aiEdit.changesApplied') | translate: { count: offer.count } }}
                </span>
                <span class="ra-island-divider"></span>
                <button
                    type="button"
                    class="ra-island-action"
                    [disabled]="store.reverting()"
                    (click)="store.undo(offer.taskId)">
                    {{ 'document.edit.aiEdit.undo' | translate }}
                </button>
            </ra-island>
        } @else if (store.attention(); as attention) {
            <ra-island class="ai-island" role="status" animate.enter="island-in" animate.leave="island-out">
                <span class="island-dot" [class.island-dot--error]="!attention.conflicts" aria-hidden="true"></span>
                <span class="ra-island-text">
                    {{ 'document.edit.aiEdit.islandAttention' | translate: { done: attention.applied, total: attention.total } }}
                    @if (attention.conflicts) {
                        · {{ (attention.conflicts === 1 ? 'document.edit.aiEdit.islandConflictsOne' : 'document.edit.aiEdit.islandConflicts') | translate: { count: attention.conflicts } }}
                    }
                    @if (attention.errors) {
                        · {{ (attention.errors === 1 ? 'document.edit.aiEdit.islandErrorsOne' : 'document.edit.aiEdit.islandErrors') | translate: { count: attention.errors } }}
                    }
                </span>
            </ra-island>
        }
    `,
    styles: `
        :host {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 1rem;
            z-index: 3;
            display: flex;
            justify-content: center;
            padding: 0 1rem;
            pointer-events: none;
        }

        .ai-island {
            pointer-events: auto;
            min-height: 2.375rem;
        }

        // Estado 4: a mesma ilha, em verde (sucesso).
        .ai-island--success {
            padding-left: 0.875rem;
            background-color: var(--accentGreen);
            color: var(--contentInverse);
        }

        .island-spinner {
            width: 0.75rem;
            height: 0.75rem;
            margin-left: 0.25rem;
            box-sizing: border-box;
            border: 2px solid currentColor;
            border-top-color: transparent;
            border-radius: 50%;
            animation: island-spin 0.8s linear infinite;
        }

        // Estados 5 e 6: um ponto laranja (conflito) ou vermelho (só falhas).
        .island-dot {
            width: 0.5rem;
            height: 0.5rem;
            margin-left: 0.25rem;
            border-radius: 50%;
            background: var(--accentOrange);

            &--error {
                background: var(--accentRed);
            }
        }

        .island-in {
            animation: island-fade-up 0.25s ease-out both;
        }

        .island-out {
            animation: island-fade-up 0.2s ease-in reverse both;
        }

        @keyframes island-spin {
            to {
                transform: rotate(360deg);
            }
        }

        @keyframes island-fade-up {
            from {
                opacity: 0;
                transform: translateY(0.5rem);
            }

            to {
                opacity: 1;
                transform: none;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .island-spinner {
                animation: none;
                border-top-color: currentColor;
            }

            .island-in,
            .island-out {
                animation-name: island-fade;
            }

            @keyframes island-fade {
                from {
                    opacity: 0;
                }

                to {
                    opacity: 1;
                }
            }
        }
    `,
})
export class AiEditPreviewIslandComponent {
    public store = inject(AiEditStore);

    public total = computed(() => this.store.changes().length);
    public applying = computed(() => this.store.stage() === 'applying' && this.total() > 0);
}
